import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
    SaveOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
} from "@ant-design/icons";
import { Space, Form, Input, Button, Alert, message, Table, Tag } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { getDots, updateDotsAppId } from "components/brands/brands.api";
import { DOTS_APP_QUERY_KEY } from "common/query-keys";
import { isValidGuid } from "common/utils";

// Component for expandable filter types
const ExpandableFilterTypes = ({ filterTypes }) => {
    const [expanded, setExpanded] = useState(false);

    if (!filterTypes || filterTypes.length === 0) return "-";

    const maxVisible = 2;
    const visibleTypes = expanded
        ? filterTypes
        : filterTypes.slice(0, maxVisible);
    const remainingCount = filterTypes.length - maxVisible;

    return (
        <div style={{ lineHeight: "1.4" }}>
            {visibleTypes.map((type, index) => (
                <div key={index} style={{ marginBottom: "2px" }}>
                    <Tag color="blue" size="small">
                        {type}
                    </Tag>
                </div>
            ))}
            {!expanded && remainingCount > 0 && (
                <div style={{ marginTop: "2px" }}>
                    <Tag
                        color="default"
                        size="small"
                        style={{ cursor: "pointer" }}
                        onClick={() => setExpanded(true)}>
                        + {remainingCount} More
                    </Tag>
                </div>
            )}
            {expanded && filterTypes.length > maxVisible && (
                <div style={{ marginTop: "2px" }}>
                    <Tag
                        color="default"
                        size="small"
                        style={{ cursor: "pointer" }}
                        onClick={() => setExpanded(false)}>
                        Show Less
                    </Tag>
                </div>
            )}
        </div>
    );
};

const AutomatedPayouts = ({ brandUser }) => {
    const [form] = Form.useForm();
    const [dotsAppId, setDotsAppId] = useState();
    const [hasDotsAppId, setHasDotsAppId] = useState(true);
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        const fetchDotsAppId = async () => {
            try {
                const data = await getDots(brandUser.userId);

                if (data.success && data.result && data.result.id) {
                    setHasDotsAppId(true);
                    setDotsAppId(data.result.id);
                    form.setFieldValue("dotsAppId", data.result.id);
                } else {
                    setHasDotsAppId(false);
                    setDotsAppId("");

                    form.setFieldValue("dotsAppId", "");
                }
            } catch (error) {
                console.error("Failed to get Dots App ID:", error);
            }
        };

        if (brandUser?.userId) {
            fetchDotsAppId();
        }
    }, [brandUser?.userId]);

    const handleSave = async (values) => {
        setLoading(true);
        try {
            const data = await updateDotsAppId(
                values.dotsAppId,
                brandUser.userId,
            );
            if (data.success) {
                message.success("Dots configuration was successfully updated!");
                setDotsAppId(values.dotsAppId);
                setHasDotsAppId(!!values.dotsAppId);
                // The Feature Flags tab reads this to decide whether enabling automated
                // payments needs a warning. Queries don't refetch on mount, so without
                // this the stale "no app" answer survives the tab switch.
                queryClient.invalidateQueries({
                    queryKey: [DOTS_APP_QUERY_KEY, String(brandUser.userId)],
                });
            } else {
                message.error(data.message);
            }
        } catch (error) {
            message.error(
                "Failed to save Dots configuration. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {!hasDotsAppId && (
                <Alert
                    style={{ width: "100%" }}
                    message="This account is not connected to Automated Payouts. Please enter your DOTS App ID to enable Automated Payouts."
                    type="warning"
                    showIcon
                />
            )}
            <Form
                className="brand-profile-form"
                form={form}
                autoComplete="off"
                layout="vertical"
                onFinish={handleSave}>
                <Form.Item
                    label="Dots App ID"
                    name="dotsAppId"
                    tooltip={
                        hasDotsAppId
                            ? "Dots App ID cannot be modified once set"
                            : "Set your Dots App ID to enable Automated Payouts"
                    }
                    initialValue={dotsAppId}
                    validateTrigger={["onChange", "onBlur"]}
                    style={{ width: 335 }}
                    rules={[
                        {
                            validator: (_, value) => {
                                if (!value || isValidGuid(value)) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(
                                    new Error(
                                        "Invalid Dots App ID. Please enter a valid Dots App ID.",
                                    ),
                                );
                            },
                        },
                    ]}>
                    <Input
                        readOnly={hasDotsAppId}
                        placeholder={
                            hasDotsAppId
                                ? "Dots App ID is set and cannot be modified"
                                : "Enter your Dots App ID"
                        }
                        value={hasDotsAppId ? dotsAppId : undefined}
                        disabled={hasDotsAppId}
                    />
                </Form.Item>

                <Form.Item className="button-form-item" shouldUpdate>
                    {({ getFieldValue, getFieldsError }) => {
                        const currentDotsAppId = getFieldValue("dotsAppId");

                        const hasChanged = currentDotsAppId !== dotsAppId;
                        const hasErrors =
                            getFieldsError().filter(
                                ({ errors }) => errors.length,
                            ).length > 0;

                        return (
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={!hasChanged || hasErrors}
                                loading={loading}
                                icon={<SaveOutlined />}>
                                Save
                            </Button>
                        );
                    }}
                </Form.Item>
            </Form>
        </Space>
    );
};

export default AutomatedPayouts;
